import { differenceInMonths, differenceInYears, format, isMatch, parse } from "date-fns";

/**
 * HTML date inputs are not consistent about restricting the year to four digits.
 * Keep API-bound date-only values in the ISO YYYY-MM-DD form regardless of browser.
 */
const isValidISODate = (value: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) && isMatch(value, 'yyyy-MM-dd');

const parseJSONDateWithoutTime = (date: string) => {
    const datePortion = date.split('T')[0];    
    return parse(datePortion, 'yyyy-MM-dd', new Date());
}

const formatOptionalJSONDateWithoutTime = (
    date: string | null | undefined,
    desiredFormat: string,
    silentlyFail = false
) => {
    if (!date || !date.trim()) {
        return '';
    }

    try{
        const parsedDate = parseJSONDateWithoutTime(date);
        return format(parsedDate, desiredFormat);
    } catch(e) {
        console.error(`Failed to format JSON date without time. Date: '${date}', DesiredFormat: ${desiredFormat}`, e);   
        if (silentlyFail) {
            return '';
        }  
        throw e;
    }
};

const formatJSONDateWithoutTime = (
    date: string | null | undefined,
    desiredFormat: string,
    silentlyFail = false
) => formatOptionalJSONDateWithoutTime(date, desiredFormat, silentlyFail);

const getDisplayAgeFromDateOfBirth = (
    dateOfBirth: string | null | undefined,
    currentDate = new Date()
) => {
    if (!dateOfBirth || !dateOfBirth.trim()) {
        return '';
    }

    try {
        const parsedDate = parseJSONDateWithoutTime(dateOfBirth);
        const totalMonths = Math.max(0, differenceInMonths(currentDate, parsedDate));

        if (totalMonths < 24) {
            return `${totalMonths} ${totalMonths === 1 ? 'month' : 'months'}`;
        }

        const totalYears = Math.max(0, differenceInYears(currentDate, parsedDate));
        return `${totalYears} ${totalYears === 1 ? 'year' : 'years'}`;
    } catch (e) {
        console.error(`Failed to calculate age from date of birth. Date: '${dateOfBirth}'`, e);
        return '';
    }
};



export {
    parseJSONDateWithoutTime,
    formatJSONDateWithoutTime,
    formatOptionalJSONDateWithoutTime,
    getDisplayAgeFromDateOfBirth,
    isValidISODate
}
